(() => {
  const root = document.querySelector('[data-ceooud-email-capture]');
  if (!root || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';

  const teaser = root.querySelector('[data-ceooud-email-teaser]');
  const backdrop = root.querySelector('[data-ceooud-email-backdrop]');
  const dialog = root.querySelector('[role="dialog"]');
  const openButton = root.querySelector('[data-ceooud-email-open]');
  const dismissButton = root.querySelector('[data-ceooud-email-dismiss]');
  const closeButtons = root.querySelectorAll('[data-ceooud-email-close]');
  const isPopupSubmission = window.location.hash === '#CEOOUDEmailCaptureForm';
  const success = isPopupSubmission && root.dataset.formSuccess === 'true';
  const hasErrors = isPopupSubmission && root.dataset.formErrors === 'true';
  const alreadySubscribed = root.dataset.customerSubscribed === 'true';
  const dismissKey = 'ceooud_welcome_offer_dismissed_at_v3_' + root.dataset.themeId;
  const subscribedKey = 'ceooud_welcome_offer_subscribed';
  const modalShownKey = 'ceooud_welcome_offer_modal_shown_v3_' + root.dataset.themeId;
  const dismissalWindow = 3 * 24 * 60 * 60 * 1000;
  const isolatedElements = new Map();
  let lastFocusedElement = null;
  let previousBodyOverflow = '';

  if (!teaser || !backdrop || !dialog || !openButton || !dismissButton) return;

  const setModalIsolation = (enabled) => {
    if (enabled) {
      isolatedElements.clear();
      Array.from(document.body.children)
        .filter(
          (element) =>
            element !== root &&
            !element.contains(root) &&
            !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)
        )
        .forEach((element) => {
          isolatedElements.set(element, element.hasAttribute('inert'));
          element.setAttribute('inert', '');
        });
    } else {
      isolatedElements.forEach((wasInert, element) => {
        element.toggleAttribute('inert', wasInert);
      });
      isolatedElements.clear();
    }
    teaser.toggleAttribute('inert', enabled);
  };

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // The popup continues to work when storage is unavailable.
      }
    },
  };

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        // The popup continues to work when session storage is unavailable.
      }
    },
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const showTeaser = () => {
    root.classList.add('is-teaser-visible');
    teaser.setAttribute('aria-hidden', 'false');
  };

  const hideTeaser = () => {
    root.classList.remove('is-teaser-visible');
    teaser.setAttribute('aria-hidden', 'true');
  };

  const openDialog = () => {
    lastFocusedElement = document.activeElement;
    hideTeaser();
    setModalIsolation(true);
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    backdrop.hidden = false;
    requestAnimationFrame(() => {
      backdrop.classList.add('is-open');
      const focusTarget =
        root.querySelector('[data-ceooud-email-success]') ||
        dialog.querySelector('[aria-invalid="true"]') ||
        dialog.querySelector('input:not([type="hidden"]):not([disabled])') ||
        dialog;
      focusTarget.focus({ preventScroll: true });
    });
  };

  const closeDialog = () => {
    storage.set(modalShownKey, String(Date.now()));
    backdrop.classList.remove('is-open');
    document.body.style.overflow = previousBodyOverflow;
    window.setTimeout(() => {
      backdrop.hidden = true;
      setModalIsolation(false);
      const shouldShowTeaser =
        !success && !alreadySubscribed && storage.get(subscribedKey) !== 'true' && Date.now() - Number(storage.get(dismissKey) || 0) > dismissalWindow;
      if (shouldShowTeaser) showTeaser();

      const lastFocusIsAvailable =
        lastFocusedElement instanceof HTMLElement &&
        lastFocusedElement.isConnected &&
        !lastFocusedElement.closest('[hidden]') &&
        !lastFocusedElement.closest('[inert]');
      const returnFocusTarget = lastFocusIsAvailable
        ? lastFocusedElement
        : shouldShowTeaser
          ? openButton
          : null;
      returnFocusTarget?.focus({ preventScroll: true });
    }, 230);
  };

  const dismissTeaser = () => {
    hideTeaser();
    storage.set(dismissKey, String(Date.now()));
    storage.set(modalShownKey, String(Date.now()));
  };

  const trapFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(dialog.querySelectorAll(focusableSelector)).filter(
      (element) => element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  openButton.addEventListener('click', () => {
    storage.set(modalShownKey, String(Date.now()));
    openDialog();
  });
  dismissButton.addEventListener('click', dismissTeaser);
  closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
  dialog.addEventListener('keydown', trapFocus);
  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) closeDialog();
  });

  if (success) {
    storage.set(subscribedKey, 'true');
    openDialog();
    return;
  }

  if (hasErrors) {
    openDialog();
    return;
  }

  if (alreadySubscribed || storage.get(subscribedKey) === 'true') return;

  const dismissedAt = Number(storage.get(dismissKey) || 0);
  if (dismissedAt && Date.now() - dismissedAt < dismissalWindow) return;

  window.addEventListener('storage', (event) => {
    if (event.key === dismissKey || event.key === subscribedKey) {
      if (!backdrop.hidden) closeDialog();
      hideTeaser();
    }
  });
  showTeaser();

  if (Date.now() - Number(storage.get(modalShownKey) || 0) > 86400000) {
    window.setTimeout(() => {
      if (Date.now() - Number(storage.get(modalShownKey) || 0) < 86400000 || !backdrop.hidden || document.hidden || document.activeElement?.matches('input, textarea, select') || document.querySelector('#Details-menu-drawer-container[open]')) return;
      storage.set(modalShownKey, String(Date.now()));
      openDialog();
    }, 3000);
  }
})();
